import { Box, CircularProgress } from '@mui/material';
import React, { FC, useRef } from 'react';

import useClearFiltersCallback from '../../hooks/useClearFiltersCallback';
import useElementScrollTracker from '../../hooks/useElementScrollTracker';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import { IFiltersRef } from '../../types/tables';
import { IClaimLinkTemplate, ITemplateCardConfig } from '../../types/templates';
import { thinScrollbarStyles } from '../../utils/ui';
import { PageContent, TableEmptyPlaceholder, TableFilteredEmptyPlaceholder } from '../_mui';
import { ClaimLinkTemplateCard } from './ClaimLinkTemplateCard';

interface IProps {
  templates: IClaimLinkTemplate[];
  isLoading?: boolean;
  isFetchingMore?: boolean;
  hasNextPage?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onLoadMore: () => Promise<unknown>;
  cardConfig?: ITemplateCardConfig;
  hasFiltersApplied?: boolean;
  filtersRef?: React.RefObject<IFiltersRef>;
}

const scrollContainerStyles = {
  flex: '1 1 0%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowX: 'hidden',
  ...thinScrollbarStyles,
};

const centeredStateStyles = {
  flex: '1 1 0%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 0,
};

const gridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: 3,
  width: '100%',
  alignItems: 'stretch',
  alignSelf: 'start',
};

const loadMoreRowStyles = {
  gridColumn: '1 / -1',
  display: 'flex',
  justifyContent: 'center',
  py: 2,
};

export const ClaimLinkTemplatesInfiniteList: FC<IProps> = ({
  templates,
  isLoading = false,
  isFetchingMore = false,
  hasNextPage = false,
  isError = false,
  onRetry,
  onLoadMore,
  cardConfig,
  hasFiltersApplied,
  filtersRef,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const handleClearFilters = useClearFiltersCallback(filtersRef ?? null);

  const handleScroll = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage: onLoadMore,
  });

  useElementScrollTracker({
    trigger: !isLoading && hasNextPage && !isFetchingMore,
    ref: containerRef,
    callback: onLoadMore,
  });

  if (!templates.length && isLoading) {
    return (
      <Box sx={centeredStateStyles} role="status" aria-label="Loading claim link templates">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError && !templates.length) {
    return (
      <TableEmptyPlaceholder
        item="claim link templates"
        title="We couldn't load claim link templates"
        subtitle="Something went wrong on our side. Try again in a moment."
        buttonText={onRetry ? 'Try again' : undefined}
        onButtonClick={onRetry}
      />
    );
  }

  if (!templates.length && !hasNextPage && !isFetchingMore) {
    if (hasFiltersApplied) {
      return <TableFilteredEmptyPlaceholder itemType="claim link templates" onClearFilters={handleClearFilters} />;
    }

    return (
      <TableEmptyPlaceholder
        item="claim link templates"
        title="No claim link templates yet."
        subtitle="Save your changes, then go to Experience Manager → Claim Link Templates to create one. Come back here to select it once it's ready."
      />
    );
  }

  return (
    <Box ref={containerRef} onScroll={handleScroll} sx={scrollContainerStyles} data-action="templates_scroll_area">
      <PageContent>
        <Box sx={gridStyles}>
          {templates.map((template) => (
            <ClaimLinkTemplateCard key={template.uid} template={template} config={cardConfig} />
          ))}
          {isFetchingMore && (
            <Box sx={loadMoreRowStyles} role="status" aria-label="Loading more claim link templates">
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </PageContent>
    </Box>
  );
};
